'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { House } from 'lucide-react';

import type { TBreadcrumbStep } from '@/utils/breadcrumbTrail';

/**
 * A step with no `href` renders as text, so a segment that is not a page can
 * never be a link (spec 010, FR-002). Re-exported for call sites that already
 * import from here.
 */
export type { TBreadcrumbStep };


type BreadcrumbsProps = {
  lastItemTitle?: string;
  /**
   * An explicit trail, INCLUDING the home step (`{ title: '', href: '/' }`).
   *
   * Supplied by pages whose URL is not the same thing as its hierarchy. The lot
   * page is why this exists: it used to live at `/aukcje/lot/<slug>`, and
   * deriving the trail from the path produced a `Lot` crumb linking to
   * `/aukcje/lot`, which has no page behind it. Deriving from the path also
   * reads the label off the slug, so a make crumb said `Bmw` rather than `BMW`.
   *
   * Passing the trail means the page that knows what exists decides what is
   * clickable — and the same array feeds `BreadcrumbSchema`, so the visible and
   * structured trails cannot disagree (FR-003, FR-008).
   */
  items?: TBreadcrumbStep[];
};

// Known route segments whose auto-formatted label needs an override (e.g. casing).
const SEGMENT_LABELS: Record<string, string> = {
  'obslugiwane-miasta': 'Obsługiwane miasta',
};

function formatSlug(slug: string): string {
  if (SEGMENT_LABELS[slug]) return SEGMENT_LABELS[slug];
  const spaced = slug.replace(/-/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ lastItemTitle, items }) => {
  const pathname = usePathname();

  const segments = pathname
    .split('/')
    .filter(Boolean)
    .map((_, i, arr) => '/' + arr.slice(0, i + 1).join('/'));

  const derived: TBreadcrumbStep[] = segments.map((path, i) => {
    const slug = path.split('/').pop() || '';
    const isLast = i === segments.length - 1;

    return {
      href: path,
      title: isLast && lastItemTitle ? lastItemTitle : formatSlug(decodeURIComponent(slug)),
    };
  });

  const fullItems: TBreadcrumbStep[] = items ?? [{ href: '/', title: '' }, ...derived];

  if (fullItems.length <= 1) return null;

  return (
    <nav aria-label="Breadcrumb" className="w-full overflow-hidden">
      <ol className="flex items-center text-sm text-neutral-400 space-x-2 overflow-hidden">
        {fullItems.map((item, index) => {
          const isLast = index === fullItems.length - 1;
          const isHome = index === 0;

          return (
            <li
              key={item.href ?? `${index}-${item.title}`}
              className={`flex items-center ${isLast ? 'flex-1 min-w-0' : 'shrink-0'}`}
            >
              {index > 0 && <span className="mx-2 text-neutral-600 shrink-0">/</span>}

              {isLast || !item.href ? (
                <span
                  className="truncate text-action block overflow-hidden whitespace-nowrap w-full"
                  title={item.title}
                >
                  {item.title}
                </span>
              ) : (
                <Link href={item.href} className="relative flex items-center group whitespace-nowrap shrink-0">
                  {isHome ? (
                    <>
                      <House className="w-4 h-4 shrink-0 text-current" />
                      <span className="hidden absolute bottom-0 left-0 w-full h-[1px] bg-current group-hover:block" />
                    </>
                  ) : (
                    <span className="group-hover:underline">{item.title}</span>
                  )}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
